import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { notification } from "antd";
import { websocketClient } from "../services/websocket";

/**
 * Custom hook for real-time updates via WebSocket
 * Listens to sandbox events and updates React Query cache accordingly
 */
export const useRealTimeUpdates = () => {
  const queryClient = useQueryClient();
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);

  useEffect(() => {
    const socket = websocketClient.connect();

    // If WebSocket is disabled, set states and return early
    if (!socket) {
      setIsConnected(false);
      setIsConnecting(false);
      return () => {}; // No cleanup needed
    }

    setIsConnecting(true);

    // Connection state handlers
    socket.on("connect", () => {
      setIsConnected(true);
      setIsConnecting(false);
      console.log("[RealTime] Connected to WebSocket");
    });

    socket.on("disconnect", () => {
      setIsConnected(false);
      setIsConnecting(false);
      console.log("[RealTime] Disconnected from WebSocket");
    });

    socket.on("reconnect_attempt", () => {
      setIsConnecting(true);
    });

    socket.on("reconnect", () => {
      setIsConnected(true);
      setIsConnecting(false);
      notification.success({
        message: "Real-time Updates Restored",
        description: "Connection to real-time updates has been re-established",
        duration: 3,
      });
    });

    // Sandbox event handlers
    socket.on("sandbox.created", (event: any) => {
      console.log("[RealTime] sandbox.created:", event);

      // Invalidate list to refetch with new sandbox
      queryClient.invalidateQueries({ queryKey: ["default", "sandboxes", "list"] });

      notification.info({
        message: "New Sandbox Created",
        description: `Sandbox ${event.detail?.sandbox_id || "unknown"} has been created`,
        duration: 4,
      });
    });

    socket.on("sandbox.ready", (event: any) => {
      console.log("[RealTime] sandbox.ready:", event);

      const sandboxId = event.detail?.sandbox_id;

      if (sandboxId) {
        // Update specific sandbox in cache
        queryClient.setQueryData(
          ["default", "sandboxes", "getOne", sandboxId],
          (old: any) => {
            if (!old) return old;
            return {
              ...old,
              data: {
                ...old.data,
                status: "ready",
                updated_at: new Date().toISOString(),
              },
            };
          }
        );

        // Invalidate list to refresh
        queryClient.invalidateQueries({ queryKey: ["default", "sandboxes", "list"] });

        notification.success({
          message: "Sandbox Ready",
          description: `Sandbox ${sandboxId} is now ready to use!`,
          duration: 5,
        });
      }
    });

    socket.on("sandbox.failed", (event: any) => {
      console.log("[RealTime] sandbox.failed:", event);

      const sandboxId = event.detail?.sandbox_id;
      const error = event.detail?.error || "Unknown error";

      if (sandboxId) {
        // Update specific sandbox in cache
        queryClient.setQueryData(
          ["default", "sandboxes", "getOne", sandboxId],
          (old: any) => {
            if (!old) return old;
            return {
              ...old,
              data: {
                ...old.data,
                status: "failed",
                status_message: error,
                updated_at: new Date().toISOString(),
              },
            };
          }
        );

        // Invalidate list to refresh
        queryClient.invalidateQueries({ queryKey: ["default", "sandboxes", "list"] });

        notification.error({
          message: "Sandbox Failed",
          description: `Sandbox ${sandboxId} failed: ${error}`,
          duration: 10,
        });
      }
    });

    socket.on("sandbox.deleted", (event: any) => {
      console.log("[RealTime] sandbox.deleted:", event);

      const sandboxId = event.detail?.sandbox_id;

      if (sandboxId) {
        // Remove from specific query cache
        queryClient.removeQueries({ queryKey: ["default", "sandboxes", "getOne", sandboxId] });

        // Invalidate list to refresh
        queryClient.invalidateQueries({ queryKey: ["default", "sandboxes", "list"] });

        notification.info({
          message: "Sandbox Deleted",
          description: `Sandbox ${sandboxId} has been deleted`,
          duration: 4,
        });
      }
    });

    // Cleanup on unmount
    return () => {
      console.log("[RealTime] Cleaning up WebSocket listeners");
      socket.off("connect");
      socket.off("disconnect");
      socket.off("reconnect_attempt");
      socket.off("reconnect");
      socket.off("sandbox.created");
      socket.off("sandbox.ready");
      socket.off("sandbox.failed");
      socket.off("sandbox.deleted");
      socket.disconnect();
    };
  }, [queryClient]);

  return { isConnected, isConnecting };
};
